import "gridstack/dist/gridstack.min.css";
import { forwardRef, useEffect, useImperativeHandle, useRef, type CSSProperties, type MouseEvent } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsAlt, faCopy, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { GridItemHTMLElement, GridStack } from "gridstack";
import { IComponent, IContentComponent, IContentTemplateHandle, IRefreshComponentPreviewHandle } from "../../../models/Interfaces";
import { SiteSwitcher } from "../../../services/SiteSwitcher";
import { ContentEditorService } from "../../../services/ContentEditorService";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";
import ComponentPreview from "./ComponentPreview";
import { changedGridCells, type IGridCell } from "./gridLayoutSync";
import { useConfirm } from "../../../config/ConfirmProvider";

interface IGridStackTemplate {
  ContentComponents: Array<IContentComponent> | null;
  AvailableComponents: Array<IComponent>;
  EditComponent: (component: IContentComponent, propertyName?: string) => void;
  EditingComponentId?: number;
  DraftComponentIds?: number[];
  ComponentDrafts?: Record<number, IContentComponent>;
  DuplicateComponent: (component: IContentComponent) => void;
  DuplicatingComponentId?: number;
  OnInlineDirtyChange?: (id: number, dirty: boolean) => void;
  SaveInlineComponent: (component: IContentComponent) => Promise<boolean>;
  PageCss?: string;
  SectionId: number;
  VersionId: number;
  PageClass?: string;
  SectionClass?: string;
  SectionAnchor?: string;
  PageStyle?: CSSProperties;
  SectionStyle?: CSSProperties;
  onComponentsChange: (
    sectionId: number,
    updater: (components: Array<IContentComponent>) => Array<IContentComponent>
  ) => void;
  enqueueLayoutSave: (componentIds: Array<number>, save: () => Promise<unknown>) => void;
}

const EMPTY_GRID_ROWS = 10;
const EMPTY_COMPONENTS: Array<IContentComponent> = [];

const gridStackOptions = {
  column: 12,
  cellHeight: 10,
  margin: 8,
  // Keep the row the author drags to. Gravity would pack every item back to y=0.
  float: true,
  staticGrid: false,
  animate: true,
  draggable: {
    handle: ".gs-drag-handle"
  },
  resizable: {
    handles: "n, e, s, w, ne, se, sw, nw"
  }
};

const placedWidth = (component: IContentComponent): number => component.w > 0 ? component.w : 2;
const placedHeight = (component: IContentComponent): number => component.h > 0 ? component.h : 10;

const minRowFor = (components: Array<IContentComponent>): number => {
  if (components.length === 0) {
    return EMPTY_GRID_ROWS;
  }

  return components.reduce((max, component) => Math.max(max, (component.y ?? 0) + placedHeight(component)), 1);
};

const sidebarDragOrigin = (clientX: number, clientY: number): { x: number; y: number } => {
  const offsetX = Number(document.body.dataset.sidebarDragOffsetX);
  const offsetY = Number(document.body.dataset.sidebarDragOffsetY);

  if (!Number.isFinite(offsetX) || !Number.isFinite(offsetY)) {
    return { x: clientX, y: clientY };
  }

  return {
    x: clientX - offsetX,
    y: clientY - offsetY
  };
};

// Hit slop above the grid edge so a card whose top is just over the line still lands on row 0.
const FIRST_ROW_DROP_BUFFER = 8;

const GridStackTemplate = forwardRef<IContentTemplateHandle, IGridStackTemplate>((props, ref) => {
  const gridElemRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<GridStack | null>(null);
  const placeholderRef = useRef<GridItemHTMLElement | null>(null);
  const dragPointerRef = useRef<{ x: number; y: number } | null>(null);
  const dragFrameRef = useRef<number | null>(null);
  const lastPlaceholderCellRef = useRef<{ x: number; y: number } | null>(null);
  const siteSwitcherRef = useRef<SiteSwitcher>();
  const contentEditSvcRef = useRef<ContentEditorService>();
  const { requestConfirmation } = useConfirm();

  const contentComponents = props.ContentComponents ?? EMPTY_COMPONENTS;
  const componentsRef = useRef<Array<IContentComponent>>(contentComponents);
  componentsRef.current = contentComponents;
  const onComponentsChangeRef = useRef(props.onComponentsChange);
  onComponentsChangeRef.current = props.onComponentsChange;
  const enqueueLayoutSaveRef = useRef(props.enqueueLayoutSave);
  enqueueLayoutSaveRef.current = props.enqueueLayoutSave;
  const previewRefs = useRef<{ [key: string]: IRefreshComponentPreviewHandle | null }>({});

  const withGridPosition = (component: IContentComponent, x: number, y: number, w: number, h: number): IContentComponent => {
    return {
      ...component,
      PosX: x,
      PosY: y,
      Cols: w,
      Rows: h,
      x,
      y,
      w,
      h
    };
  };

  useImperativeHandle(ref, () => ({
    refreshComponent(component: IContentComponent) {
      if (!component) {
        return;
      }

      previewRefs.current[`PREVIEW-${component.Id}`]?.refreshPreview();
    }
  }));

  const deleteComponent = async (componentId: number): Promise<void> => {
    if (!contentEditSvcRef.current) {
      return;
    }

    const confirmed = await requestConfirmation(
      "Delete this component from the section?<br /><b>This action cannot be undone.</b>",
      "Delete Component"
    );

    if (!confirmed) {
      return;
    }

    const res = await contentEditSvcRef.current.DeleteContentComponent(componentId);

    if (res) {
      onComponentsChangeRef.current(props.SectionId, (components) => components.filter((component) => component.Id !== componentId));
    }
  };

  const editComponentContent = (component: IContentComponent): void => {
    props.EditComponent(component);
  };

  const editOnDoubleClick = (event: MouseEvent, component: IContentComponent): void => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest("[data-designer-action='delete'], .ui-resizable-handle")) {
      return;
    }
    editComponentContent(component);
  };

  const onComponentResize = async (_evt: Event, _gridItemElement: GridItemHTMLElement): Promise<void> => {
    const grid = gridRef.current;

    if (!contentEditSvcRef.current || !grid) {
      return;
    }

    const sectionId = Number(grid.el.dataset.section);

    if (Number.isNaN(sectionId)) {
      return;
    }

    // A drop pushes overlapping neighbors. Saving only the pointer block lets the next
    // rebuild collide with their old cells and walk the dropped block back down.
    const stored = componentsRef.current.map((component): IGridCell => ({
      id: component.Id,
      x: component.x ?? component.PosX ?? 0,
      y: component.y ?? component.PosY ?? 0,
      w: placedWidth(component),
      h: placedHeight(component)
    }));
    const live: IGridCell[] = [];

    grid.getGridItems().forEach((item) => {
      if (item.classList.contains("designer-grid-placeholder")) {
        return;
      }

      const id = Number(item.dataset.componentid);
      const node = item.gridstackNode;

      if (!Number.isInteger(id) || !node) {
        return;
      }

      live.push({
        id,
        x: node.x ?? 0,
        y: node.y ?? 0,
        w: node.w ?? 2,
        h: node.h ?? 10
      });
    });

    const changed = changedGridCells(stored, live);

    if (changed.length === 0) {
      return;
    }

    const changedById = new Map(changed.map((cell) => [cell.id, cell]));
    const previousById = new Map(componentsRef.current.map((component) => [component.Id, component]));

    onComponentsChangeRef.current(sectionId, (components) =>
      components.map((component) => {
        const cell = changedById.get(component.Id);

        return cell ? withGridPosition(component, cell.x, cell.y, cell.w, cell.h) : component;
      })
    );

    changed.forEach((cell) => {
      const previous = previousById.get(cell.id);

      enqueueLayoutSaveRef.current([cell.id], async () => {
        const moved = await contentEditSvcRef.current?.MoveGridComponent(
          sectionId,
          cell.id,
          cell.x,
          cell.y,
          cell.w,
          cell.h
        );

        if (moved || !previous) {
          return;
        }

        onComponentsChangeRef.current(sectionId, (components) =>
          components.map((component) => {
            if (component.Id !== cell.id) {
              return component;
            }

            if (component.PosX !== cell.x || component.PosY !== cell.y || component.Cols !== cell.w || component.Rows !== cell.h) {
              return component;
            }

            return withGridPosition(
              component,
              previous.x ?? previous.PosX,
              previous.y ?? previous.PosY,
              previous.w ?? previous.Cols,
              previous.h ?? previous.Rows
            );
          })
        );
      });
    });
  };

  const removePlaceholder = (): void => {
    if (placeholderRef.current && gridRef.current) {
      gridRef.current.removeWidget(placeholderRef.current, true, false);
      placeholderRef.current = null;
      lastPlaceholderCellRef.current = null;
    }
  };

  const getCellFromPoint = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const grid = gridRef.current;
    const gridElement = gridElemRef.current;

    if (!grid || !gridElement) {
      return null;
    }

    const rect = gridElement.getBoundingClientRect();
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;

    if (relX < 0 || relX > rect.width || relY < -FIRST_ROW_DROP_BUFFER) {
      return null;
    }

    if (rect.width <= 0) {
      return null;
    }

    const cols = grid.getColumn();
    const cellHeight = typeof grid.opts.cellHeight === "number" ? grid.opts.cellHeight : 10;
    const y = relY < 0
      ? 0
      : Math.max(0, Math.floor(relY / cellHeight));

    return {
      x: Math.min(Math.max(0, Math.floor(relX / (rect.width / cols))), cols - 1),
      y
    };
  };

  const resolveDropCell = (clientX: number, clientY: number): { x: number; y: number; w: number; h: number } | null => {
    const origin = sidebarDragOrigin(clientX, clientY);
    const eventCell = getCellFromPoint(origin.x, origin.y);
    const lastPointer = dragPointerRef.current;
    const lastOrigin = lastPointer ? sidebarDragOrigin(lastPointer.x, lastPointer.y) : null;
    const pointerCell = eventCell
      ?? (lastOrigin ? getCellFromPoint(lastOrigin.x, lastOrigin.y) : null)
      ?? lastPlaceholderCellRef.current;

    if (pointerCell) {
      return { x: pointerCell.x, y: pointerCell.y, w: 3, h: 10 };
    }

    const placeholderNode = placeholderRef.current?.gridstackNode;

    if (!placeholderNode) {
      return null;
    }

    return {
      x: placeholderNode.x ?? 0,
      y: placeholderNode.y ?? 0,
      w: placeholderNode.w ?? 3,
      h: placeholderNode.h ?? 10
    };
  };

  const ensurePlaceholder = (clientX: number, clientY: number): void => {
    const grid = gridRef.current;
    const cell = getCellFromPoint(clientX, clientY);

    if (!grid || !cell) {
      return;
    }

    const placeholderWidth = 3;
    const placeholderHeight = 10;

    if (!placeholderRef.current) {
      const placeholder = document.createElement("div") as GridItemHTMLElement;
      placeholder.className = "grid-stack-item designer-grid-placeholder";
      placeholder.innerHTML = `<div class="grid-stack-item-content designer-grid-placeholder__inner"></div>`;
      placeholderRef.current = grid.makeWidget(placeholder, {
        x: cell.x,
        y: cell.y,
        w: placeholderWidth,
        h: placeholderHeight,
        noMove: true,
        noResize: true,
        locked: true
      });
      lastPlaceholderCellRef.current = { x: cell.x, y: cell.y };
      return;
    }

    const lastCell = lastPlaceholderCellRef.current;

    if (lastCell && lastCell.x === cell.x && lastCell.y === cell.y) {
      return;
    }

    grid.update(placeholderRef.current, { x: cell.x, y: cell.y, w: placeholderWidth, h: placeholderHeight });
    lastPlaceholderCellRef.current = { x: cell.x, y: cell.y };
  };

  const addSidebarComponent = async (component: IComponent, x: number, y: number): Promise<void> => {
    if (!contentEditSvcRef.current) {
      return;
    }

    const cell = resolveDropCell(x, y);

    removePlaceholder();

    if (!cell) {
      return;
    }

    const res = await contentEditSvcRef.current.AddComponentToGridSection(props.SectionId, component, cell.x, cell.y, cell.w, cell.h);

    if (res) {
      const normalizedComponent = withGridPosition(res, cell.x, cell.y, cell.w, cell.h);
      await contentEditSvcRef.current.MoveGridComponent(props.SectionId, normalizedComponent.Id, cell.x, cell.y, cell.w, cell.h);
      onComponentsChangeRef.current(props.SectionId, (components) => {
        const existing = components.some((component) => component.Id === normalizedComponent.Id);
        return existing
          ? components.map((component) => component.Id === normalizedComponent.Id ? normalizedComponent : component)
          : [...components, normalizedComponent];
      });
      props.EditComponent(normalizedComponent);
      actionEmitter.emit(EmitEvent.ComponentDropped, true, "component dropped in template");
    }
  };

  const initGridStack = (): void => {
    const gridElement = gridElemRef.current;

    if (!gridElement) {
      return;
    }

    const grid = GridStack.init({
      ...gridStackOptions,
      minRow: minRowFor(componentsRef.current)
    }, gridElement);
    gridRef.current = grid;
    grid.on("dragstop", onComponentResize);
    grid.on("resizestop", onComponentResize);
  };

  useEffect(() => {
    if (!siteSwitcherRef.current) {
      siteSwitcherRef.current = new SiteSwitcher();
    }

    if (!contentEditSvcRef.current) {
      contentEditSvcRef.current = new ContentEditorService();
    }

    if (gridElemRef.current) {
      initGridStack();
    }

    return () => {
      gridRef.current?.destroy(false);
      gridRef.current = null;
    };
  }, []);

  useEffect(() => {
    const gridElement = gridElemRef.current;
    const grid = gridRef.current;

    if (!gridElement || !grid) {
      return;
    }

    if (gridElement.querySelector(".ui-draggable-dragging, .ui-resizable-resizing")) {
      return;
    }

    const componentsById = new Map(contentComponents.map((component) => [String(component.Id), component]));

    // Batch restore puts the previous float mode back. Gravity would pack every block to y=0.
    grid.float(true);
    grid.updateOptions({ minRow: minRowFor(contentComponents) });
    grid.batchUpdate();
    grid.removeAll(false);

    const renderedItems = Array.from(gridElement.querySelectorAll(".grid-stack-item")) as GridItemHTMLElement[];

    renderedItems.forEach((item) => {
      const componentId = item.dataset.componentid;
      const isPlaceholder = item.classList.contains("designer-grid-placeholder");

      if (isPlaceholder) {
        return;
      }

      const component = componentId ? componentsById.get(componentId) : undefined;

      if (!component) {
        item.remove();
        return;
      }

      grid.makeWidget(item, {
        x: component.x ?? 0,
        y: component.y ?? 0,
        w: placedWidth(component),
        h: placedHeight(component)
      });
    });

    grid.batchUpdate(false);
    grid.float(true);
  }, [contentComponents]);

  useEffect(() => {
    const updatePointer = (event: DragEvent): void => {
      if (document.body.dataset.sidebarDragging !== "true") {
        return;
      }

      dragPointerRef.current = {
        x: event.clientX,
        y: event.clientY
      };
    };

    document.addEventListener("dragover", updatePointer);

    return () => {
      document.removeEventListener("dragover", updatePointer);
    };
  }, []);

  const startPlaceholderLoop = (): void => {
    if (dragFrameRef.current !== null) {
      return;
    }

    const tick = (): void => {
      if (document.body.dataset.sidebarDragging !== "true") {
        dragFrameRef.current = null;
        removePlaceholder();
        return;
      }

      const pointer = dragPointerRef.current;
      const gridElement = gridElemRef.current;

      if (pointer && gridElement) {
        const origin = sidebarDragOrigin(pointer.x, pointer.y);
        const rect = gridElement.getBoundingClientRect();
        const inHorizontalRange = origin.x >= rect.left && origin.x <= rect.right;
        const inVerticalRange = origin.y >= rect.top - FIRST_ROW_DROP_BUFFER;
        const tooFarBelow = origin.y > rect.bottom + 200;

        if (inHorizontalRange && inVerticalRange && !tooFarBelow) {
          ensurePlaceholder(origin.x, origin.y);
        } else {
          removePlaceholder();
        }
      }

      dragFrameRef.current = window.requestAnimationFrame(tick);
    };

    dragFrameRef.current = window.requestAnimationFrame(tick);
  };

  const stopPlaceholderLoop = (): void => {
    if (dragFrameRef.current !== null) {
      window.cancelAnimationFrame(dragFrameRef.current);
      dragFrameRef.current = null;
    }
  };

  const onGridDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    const hasSidebarDrag = document.body.dataset.sidebarDragging === "true";
    const hasComponentPayload = event.dataTransfer.types.includes("component") || event.dataTransfer.types.includes("text/plain");

    if (!hasSidebarDrag && !hasComponentPayload) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    dragPointerRef.current = {
      x: event.clientX,
      y: event.clientY
    };
    startPlaceholderLoop();
  };

  const onGridDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    const currentTarget = event.currentTarget;
    const relatedTarget = event.relatedTarget as Node | null;

    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      const rect = currentTarget.getBoundingClientRect();
      const exitedViaBottom =
        event.clientY >= rect.bottom &&
        event.clientX >= rect.left &&
        event.clientX <= rect.right;

      if (!exitedViaBottom) {
        stopPlaceholderLoop();
        removePlaceholder();
      }
    }
  };

  const onGridDrop = async (event: React.DragEvent<HTMLDivElement>): Promise<void> => {
    event.preventDefault();

    const componentData = event.dataTransfer.getData("component") || event.dataTransfer.getData("text/plain");

    if (!componentData) {
      stopPlaceholderLoop();
      removePlaceholder();
      return;
    }

    const parsedComponent = JSON.parse(componentData) as IComponent;
    stopPlaceholderLoop();
    await addSidebarComponent(parsedComponent, event.clientX, event.clientY);
  };

  return (
    <div
      ref={gridElemRef}
      key={`GS-${props.VersionId}-${props.SectionId}`}
      className={`grid-stack designer-grid grid-stack-section-${props.SectionId}`}
      id={`gs-grid-${props.SectionId}`}
      data-version={props.VersionId}
      data-section={props.SectionId}
      onDragOver={onGridDragOver}
      onDragLeave={onGridDragLeave}
      onDrop={onGridDrop}
    >
      {[...contentComponents]
        .sort((a, b) => (a.y - b.y) || (a.x - b.x) || (a.Id - b.Id))
        .map((component) => (
        <div
          className="grid-stack-item component-editor-container"
          title={component.DisplayName}
          key={`comp-${component.Id}`}
          gs-x={component.x}
          gs-y={component.y}
          gs-w={placedWidth(component)}
          gs-h={placedHeight(component)}
          data-componentid={component.Id}
          tabIndex={0}
          onDoubleClick={(event) => editOnDoubleClick(event, component)}
        >
          <div className="ContentEditorButtons">
            <div className="editorButtons gs-drag-handle">
              <span className="componentLabel">{component.DisplayName}</span>
              <Button
                variant="link"
                onClick={() => editComponentContent(component)}
                title="edit content"
                className="btn-nomin text-white p-0"
              >
                <FontAwesomeIcon icon={faEdit} />
              </Button>
              <Button variant="link" className="btn-nomin text-white p-0"
                title="Duplicate component" aria-label={`Duplicate ${component.DisplayName}`}
                disabled={props.DuplicatingComponentId !== undefined}
                onClick={event => { event.stopPropagation(); props.DuplicateComponent(component); }}>
                <FontAwesomeIcon icon={faCopy} />
              </Button>
              <Button
                variant="link"
                onClick={() => deleteComponent(component.Id)}
                title="remove content"
                data-designer-action="delete"
                className="btn-nomin text-white p-0"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
              <FontAwesomeIcon
                icon={faArrowsAlt}
                className="text-white"
                title="move content"
                style={{ cursor: "grab" }}
              />
            </div>
          </div>
          <div className="grid-stack-item-content">
            <div id={component.ApiTag} className="ItemContent overflow-hidden">
              <div className="componentpreview h-100">
                <ComponentPreview
                  ref={el => (previewRefs.current[`PREVIEW-${component.Id}`] = el)}
                  ApiTag={component.ApiTag}
                  ContentComponentId={component.Id}
                  VersionId={props.VersionId}
                  IsGrid={true}
                  Component={component}
                  DraftComponent={props.ComponentDrafts?.[component.Id]}
                  EditComponent={props.EditComponent}
                  PropertiesOpen={props.EditingComponentId === component.Id || props.DraftComponentIds?.includes(component.Id)}
                  OnInlineDirtyChange={props.OnInlineDirtyChange}
                  SaveInlineComponent={props.SaveInlineComponent}
                  pageCss={props.PageCss}
                  pageClass={props.PageClass}
                  sectionClass={props.SectionClass}
                  sectionId={props.SectionAnchor}
                  pageStyle={props.PageStyle}
                  sectionStyle={props.SectionStyle}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

export default GridStackTemplate;
